import React from 'react';
import { UserCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { NoFileResult } from '@/components/spec/demo/NoFileResult';

const short = (a) => `${a.slice(0, 8)}...${a.slice(-6)}`;
const MODES = [['real', 'Real seller agent'], ['impostor', 'Impostor seller agent'], ['nofile', 'Store has no claims file']];

function Line({ ok, label, value, id }) {
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  return (
    <li className="flex gap-3 text-sm" data-testid={id}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${ok ? 'text-emerald-400' : 'text-amber-400'}`} />
      <span className="text-gray-300">{label} <span className="font-mono text-xs text-gray-400 break-all">{value}</span></span>
    </li>
  );
}

function SignedResult({ r }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="demo-seller-result">
      <ul className="space-y-2">
        <Line ok label="Says it acts for" value="shop.example" id="demo-seller-says" />
        <Line ok label="Signed with wallet" value={short(r.signer)} id="demo-seller-signer" />
        <Line ok={r.listed} label={r.listed ? 'Listed in shop.example/.well-known/claim-records.json' : 'Not listed in shop.example/.well-known/claim-records.json'} value="" id="demo-seller-listed" />
      </ul>
      <div className={`rounded-xl border p-4 text-sm ${r.listed ? 'border-emerald-500/40 text-emerald-100/90' : 'border-amber-500/50 text-amber-100/90'}`} data-testid="demo-seller-verdict">
        {r.listed
          ? 'Confirmed. The store\'s own file lists this wallet, and the agent proved it controls it.'
          : `Warning. This agent is not listed by shop.example. It asked for claims at ${r.offeredEndpoint}. Ignore that. Claims below still go to the endpoint in the store's own file:`}
        <div className="font-mono text-xs mt-2 break-all" data-testid="demo-seller-endpoint">{r.endpoint}</div>
      </div>
    </div>
  );
}

export function DemoSellerCheck({ demo }) {
  const [mode, setMode] = React.useState('real');
  const r = demo.sellerCheck;
  return (
    <section className="rounded-2xl border border-sky-500/25 bg-sky-500/[0.04] p-6 mb-6" data-testid="demo-seller-check">
      <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2"><UserCheck className="w-4 h-4 text-sky-300" /> 0. Who am I talking to?</h2>
      <p className="text-sm text-gray-400 mb-4 max-w-3xl">Before sending a claim, the shopping agent checks that the seller agent truly acts for shop.example: it reads the store's own claims file and asks the seller agent to sign.</p>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4" data-testid="demo-seller-modes">
        {MODES.map(([id, label]) => (
          <label key={id} className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input type="radio" name="seller-mode" className="accent-amber-300" checked={mode === id} onChange={() => setMode(id)} data-testid={`demo-seller-mode-${id}`} />
            {label}
          </label>
        ))}
        <button type="button" onClick={() => demo.checkSeller(mode)} className="rounded-full bg-sky-300 text-black px-4 py-2 text-sm font-semibold hover:bg-sky-200 transition-colors" data-testid="demo-check-seller-btn">Check the seller agent</button>
      </div>
      {r && (r.noFile ? <NoFileResult /> : <SignedResult r={r} />)}
    </section>
  );
}
